import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { BeneficiarySummary, Paginated } from '@foundation/shared';
import { isValidThaiNationalId } from '@foundation/shared';
import { api, buildQuery } from '../lib/api';
import {
  BENEFICIARY_STATUS_TH,
  CATEGORY_TH,
  RISK_TH,
  formatDate,
  fullName,
  maskNationalId,
} from '../lib/format';
import { Badge, Empty, ErrorAlert, Loading, Modal } from '../components/ui';
import { useAuth } from '../auth';

interface Officer {
  id: string;
  fullName: string;
}

export default function BeneficiaryList() {
  const { allowed } = useAuth();
  const [rows, setRows] = useState<BeneficiarySummary[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(
    async (append = false, from?: string) => {
      setError(null);
      try {
        const res = await api<Paginated<BeneficiarySummary>>(
          `/beneficiaries${buildQuery({ q, status, category, limit: 50, cursor: from })}`,
        );
        setRows((prev) => (append && prev ? [...prev, ...res.data] : res.data));
        setCursor(res.nextCursor);
      } catch (e) {
        setError(e);
        if (!append) setRows([]);
      }
    },
    [q, status, category],
  );

  useEffect(() => {
    const t = setTimeout(() => void load(), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>ผู้รับความช่วยเหลือ</h1>
          <p>คุณเห็นเฉพาะข้อมูลในขอบเขตที่ได้รับมอบหมาย</p>
        </div>
        {allowed('beneficiary:write') && (
          <button onClick={() => setShowCreate(true)}>+ บันทึกผู้รับความช่วยเหลือ</button>
        )}
      </div>

      <ErrorAlert error={error} />

      <div className="card">
        <div className="toolbar">
          <div className="field grow">
            <label htmlFor="bq">ค้นหา</label>
            <input
              id="bq"
              type="search"
              placeholder="ชื่อ นามสกุล รหัส เบอร์โทร หรือเลขบัตร 4 ตัวท้าย"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="bstatus">สถานะ</label>
            <select id="bstatus" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">ทั้งหมด</option>
              {Object.entries(BENEFICIARY_STATUS_TH).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="bcat">ประเภท</label>
            <select id="bcat" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">ทั้งหมด</option>
              {Object.entries(CATEGORY_TH).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        </div>

        {rows === null ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>ยังไม่มีข้อมูลผู้รับความช่วยเหลือตามเงื่อนไขนี้</Empty>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>รหัส</th>
                    <th className="wrap">ชื่อ-สกุล</th>
                    <th>เลขบัตร</th>
                    <th>ประเภท</th>
                    <th>พื้นที่</th>
                    <th>ผู้ดูแล</th>
                    <th>ความเสี่ยง</th>
                    <th>สถานะ</th>
                    <th>วันแรกรับ</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b) => (
                    <tr key={b.id}>
                      <td className="mono">{b.benCode}</td>
                      <td className="wrap">
                        <Link className="row-link" to={`/beneficiaries/${b.id}`}>
                          {fullName(b)}
                        </Link>
                      </td>
                      <td className="mono small">{maskNationalId(b.nationalIdLast4)}</td>
                      <td>{b.category ? (CATEGORY_TH[b.category] ?? b.category) : '—'}</td>
                      <td>{b.areaName ?? '—'}</td>
                      <td>{b.primaryOfficerName ?? <span className="badge amber">ยังไม่มีผู้ดูแล</span>}</td>
                      <td>
                        {b.riskLevel ? (
                          <Badge value={b.riskLevel} label={RISK_TH[b.riskLevel] ?? b.riskLevel} />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <Badge value={b.status} label={BENEFICIARY_STATUS_TH[b.status] ?? b.status} />
                      </td>
                      <td className="small muted">{formatDate(b.intakeDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {cursor && (
              <div style={{ marginTop: 14 }}>
                <button className="secondary" onClick={() => void load(true, cursor)}>
                  โหลดเพิ่ม
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {showCreate && <CreateBeneficiaryModal onClose={() => setShowCreate(false)} />}
    </>
  );
}

/**
 * ฟอร์มบันทึกผู้รับความช่วยเหลือ
 *
 * ขั้นตอนสำคัญคือ "ตรวจก่อนบันทึก" — กรอกเลขบัตรแล้วกดตรวจสอบเพื่อดูว่ามีในระบบแล้วหรือยัง
 * ลดปัญหาบันทึกซ้ำจากเจ้าหน้าที่หลายคน (ถ้ากดบันทึกเลย backend ก็ยังกันซ้ำให้อยู่ดี)
 */
function CreateBeneficiaryModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [officers, setOfficers] = useState<Officer[]>([]);
  const [form, setForm] = useState({
    nationalId: '',
    title: 'นาง',
    firstName: '',
    lastName: '',
    birthDate: '',
    phone: '',
    addressLine: '',
    category: 'elderly',
    householdSize: '',
    primaryOfficerId: '',
    notes: '',
  });
  const [consent, setConsent] = useState({
    dataProcessing: false,
    sensitiveData: false,
    photoPublish: false,
  });
  const [dupCheck, setDupCheck] = useState<
    { state: 'idle' | 'checking' } | { state: 'result'; found: boolean; message: string; id?: string }
  >({ state: 'idle' });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<Officer[]>('/meta/officers')
      .then(setOfficers)
      .catch(() => setOfficers([]));
  }, []);

  function set<K extends keyof typeof form>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  const nidValid = form.nationalId === '' || isValidThaiNationalId(form.nationalId);

  async function checkDuplicate() {
    if (!isValidThaiNationalId(form.nationalId)) return;
    setDupCheck({ state: 'checking' });
    try {
      const res = await api<{
        found: boolean;
        inScope?: boolean;
        message?: string;
        beneficiary?: BeneficiarySummary;
      }>(`/beneficiaries/search-by-nid?nid=${encodeURIComponent(form.nationalId)}`);

      if (!res.found) {
        setDupCheck({ state: 'result', found: false, message: 'ยังไม่มีในระบบ บันทึกใหม่ได้' });
      } else if (res.inScope && res.beneficiary) {
        setDupCheck({
          state: 'result',
          found: true,
          message: `มีอยู่แล้ว: ${res.beneficiary.benCode} ${fullName(res.beneficiary)}`,
          id: res.beneficiary.id,
        });
      } else {
        setDupCheck({
          state: 'result',
          found: true,
          message: res.message ?? 'มีอยู่แล้วในระบบแต่อยู่นอกพื้นที่ที่คุณดูแล',
        });
      }
    } catch (e) {
      setError(e);
      setDupCheck({ state: 'idle' });
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api<{ id: string }>('/beneficiaries', {
        method: 'POST',
        idempotent: true,
        body: {
          nationalId: form.nationalId.trim() || undefined,
          title: form.title || undefined,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          birthDate: form.birthDate || undefined,
          phone: form.phone.trim() || undefined,
          addressLine: form.addressLine.trim() || undefined,
          category: form.category || undefined,
          householdSize: form.householdSize ? Number(form.householdSize) : undefined,
          primaryOfficerId: form.primaryOfficerId || undefined,
          notes: form.notes.trim() || undefined,
          consent: {
            dataProcessing: true,
            sensitiveData: consent.sensitiveData,
            photoPublish: consent.photoPublish,
            policyVersion: '2026.1',
            evidenceType: 'signed_form',
          },
        },
      });
      navigate(`/beneficiaries/${res.id}`);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="บันทึกผู้รับความช่วยเหลือ" onClose={onClose}>
      <form onSubmit={submit}>
        <ErrorAlert error={error} />

        <div className="field">
          <label htmlFor="nid">เลขประจำตัวประชาชน</label>
          <div className="btn-row" style={{ flexWrap: 'nowrap' }}>
            <input
              id="nid"
              inputMode="numeric"
              maxLength={17}
              value={form.nationalId}
              aria-invalid={!nidValid}
              onChange={(e) => {
                set('nationalId', e.target.value);
                setDupCheck({ state: 'idle' });
              }}
              placeholder="1234567890123"
            />
            <button
              type="button"
              className="secondary"
              disabled={!isValidThaiNationalId(form.nationalId) || dupCheck.state === 'checking'}
              onClick={() => void checkDuplicate()}
            >
              {dupCheck.state === 'checking' ? 'กำลังตรวจ…' : 'ตรวจซ้ำ'}
            </button>
          </div>
          {!nidValid && <div className="hint" style={{ color: 'var(--c-danger)' }}>เลขบัตรไม่ถูกต้อง (ตรวจจากหลักตรวจสอบ)</div>}
          {dupCheck.state === 'result' && (
            <div className={`alert ${dupCheck.found ? 'warn' : 'success'} small`} style={{ marginTop: 8, marginBottom: 0 }}>
              {dupCheck.message}
              {dupCheck.id && (
                <>
                  {' '}
                  <Link to={`/beneficiaries/${dupCheck.id}`}>เปิดข้อมูลเดิม</Link>
                </>
              )}
            </div>
          )}
          <div className="hint">
            ระบบเก็บเลขบัตรแบบเข้ารหัส แสดงผลเพียง 4 ตัวท้าย — ใช้กันการบันทึกซ้ำ
          </div>
        </div>

        <div className="form-row">
          <div className="field">
            <label htmlFor="title">คำนำหน้า</label>
            <select id="title" value={form.title} onChange={(e) => set('title', e.target.value)}>
              {['นาย', 'นาง', 'นางสาว', 'เด็กชาย', 'เด็กหญิง', ''].map((t) => (
                <option key={t || 'none'} value={t}>
                  {t || 'ไม่ระบุ'}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="firstName">ชื่อ *</label>
            <input id="firstName" required value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="lastName">นามสกุล *</label>
            <input id="lastName" required value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
          </div>
        </div>

        <div className="form-row">
          <div className="field">
            <label htmlFor="birthDate">วันเกิด</label>
            <input id="birthDate" type="date" value={form.birthDate} onChange={(e) => set('birthDate', e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="bphone">เบอร์โทร</label>
            <input id="bphone" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="0812345678" />
          </div>
        </div>

        <div className="field">
          <label htmlFor="addressLine">ที่อยู่</label>
          <input id="addressLine" value={form.addressLine} onChange={(e) => set('addressLine', e.target.value)} />
          <div className="hint">พิกัดบ้านจะบันทึกตอนเจ้าหน้าที่ลงพื้นที่ครั้งแรก (เฟส 2)</div>
        </div>

        <div className="form-row">
          <div className="field">
            <label htmlFor="cat">ประเภท</label>
            <select id="cat" value={form.category} onChange={(e) => set('category', e.target.value)}>
              {Object.entries(CATEGORY_TH).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="hh">จำนวนคนในครัวเรือน</label>
            <input id="hh" type="number" min={1} max={50} value={form.householdSize} onChange={(e) => set('householdSize', e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="officer">เจ้าหน้าที่ผู้ดูแล</label>
            <select id="officer" value={form.primaryOfficerId} onChange={(e) => set('primaryOfficerId', e.target.value)}>
              <option value="">ยังไม่ระบุ</option>
              {officers.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.fullName}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="field">
          <label htmlFor="notes">บันทึกเพิ่มเติม</label>
          <textarea id="notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </div>

        <fieldset style={{ border: '1px solid var(--c-border)', borderRadius: 8, padding: 14, marginBottom: 14 }}>
          <legend style={{ fontSize: 13.5, fontWeight: 600, padding: '0 6px' }}>
            ความยินยอมตาม PDPA
          </legend>
          <label className="checkbox" style={{ marginBottom: 8 }}>
            <input
              type="checkbox"
              required
              checked={consent.dataProcessing}
              onChange={(e) => setConsent((c) => ({ ...c, dataProcessing: e.target.checked }))}
            />
            <span>
              <strong>ยินยอมให้เก็บและใช้ข้อมูลส่วนบุคคล *</strong>
              <br />
              <span className="muted small">จำเป็นต้องได้รับก่อนบันทึก — ต้องมีแบบฟอร์มที่ลงนามเก็บไว้</span>
            </span>
          </label>
          <label className="checkbox" style={{ marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={consent.sensitiveData}
              onChange={(e) => setConsent((c) => ({ ...c, sensitiveData: e.target.checked }))}
            />
            <span>
              ยินยอมให้เก็บข้อมูลอ่อนไหว (สุขภาพ ความพิการ)
              <br />
              <span className="muted small">ถ้าไม่ยินยอม ส่วนสุขภาพในแบบสำรวจจะถูกซ่อน</span>
            </span>
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={consent.photoPublish}
              onChange={(e) => setConsent((c) => ({ ...c, photoPublish: e.target.checked }))}
            />
            <span>
              ยินยอมให้เผยแพร่ภาพเพื่อการประชาสัมพันธ์
              <br />
              <span className="muted small">ถ้าไม่ยินยอม ภาพทุกภาพจะถูกติดธง &ldquo;ห้ามเผยแพร่&rdquo;</span>
            </span>
          </label>
        </fieldset>

        <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="secondary" onClick={onClose}>
            ยกเลิก
          </button>
          <button type="submit" disabled={busy || !consent.dataProcessing || !nidValid}>
            {busy ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
